import { ReactNode, useEffect, useRef, useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import { SiGmail } from 'react-icons/si';
import { CONTACT_EMAIL } from './data';
import { useLanding } from './i18n';

/**
 * An email link that works for everyone. A plain mailto: link does nothing for visitors
 * without a mail app set up (most Gmail/Outlook web users), so a click opens a small menu:
 * copy the address, write in Gmail, or open the Mail app.
 */
export default function ContactEmail({ className, children, up = false, align = 'left', block = false }: {
  className?: string;
  children: ReactNode;
  /** Open above the link (e.g. near the bottom of the page). */
  up?: boolean;
  align?: 'left' | 'center' | 'right';
  /** Wrapper fills its container (for full-width buttons). */
  block?: boolean;
}) {
  const { t } = useLanding();
  const c = t.contact;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const root = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const subject = encodeURIComponent(c.subject);
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${subject}`;
  const gmail = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(CONTACT_EMAIL)}&su=${subject}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(CONTACT_EMAIL); } catch {
      const i = document.createElement('input');
      i.value = CONTACT_EMAIL; document.body.appendChild(i); i.select(); document.execCommand('copy'); i.remove();
    }
    setCopied(true);
    window.setTimeout(() => { setCopied(false); setOpen(false); }, 1400);
  };

  return (
    <span ref={root} className={`cm${block ? ' block' : ''}${open ? ' open' : ''}`}>
      {/* Keeps a real mailto: href so right-click "Copy email address" and no-JS still work. */}
      <a
        className={className}
        href={mailto}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
      >
        {children}
      </a>
      {open && (
        <span className={`cm-pop${up ? ' up' : ''} a-${align}`} role="menu">
          <span className="cm-hd">{c.title}</span>
          <span className="cm-addr">{CONTACT_EMAIL}</span>
          <button type="button" role="menuitem" onClick={copy}>
            {copied ? <Check className="ok" /> : <Copy />}{copied ? c.copied : c.copy}
          </button>
          <a role="menuitem" href={gmail} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <SiGmail className="gm" />{c.gmail}
          </a>
          <a role="menuitem" href={mailto} onClick={() => setOpen(false)}>
            <Mail />{c.mailApp}
          </a>
        </span>
      )}
    </span>
  );
}
